import { formatRelativeTime } from '../lib/notification-display';

/**
* Notification shape accepted by the detail view.
* Compatible with both the full Notification type and the NotificationPayload
* returned by the WebSocket / API hooks.
*/
interface DetailNotification {
id: string;
type?: string;
notificationType?: string;
title: string;
message: string;
shortformTitle?: string;
shortformMessage?: string;
relatedHoldRequestId?: string;
relatedLineItemId?: string;
isRead?: boolean;
createdAt: string;
}

interface NotificationDetailProps {
notification: DetailNotification;
onBack: () => void;
}

/**
* Format a snake_case notification type string into a human-readable label.
* e.g. "hold_request_submitted" → "Hold Request Submitted"
*/
function formatNotificationType(type: string): string {
return type
.split('_')
.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
.join(' ');
}

/**
* Parse bulk operation counts from a notification message.
* Looks for patterns like "12 items created" and "3 items failed" / "3 failed" / "3 errors".
* Returns null if no counts can be parsed.
*/
function parseBulkCounts(message: string): { success: number; failure: number } | null {
let success = 0;
let failure = 0;
let found = false;

// Match success patterns: "N items created", "N created"
const successMatch = message.match(/(\d+)\s+(?:items?\s+)?created/i);
if (successMatch && successMatch[1]) {
success = parseInt(successMatch[1], 10);
found = true;
}

// Match failure patterns: "N items failed", "N failed", "N errors"
const failureMatch = message.match(/(\d+)\s+(?:items?\s+)?(?:failed|errors?)/i);
if (failureMatch && failureMatch[1]) {
failure = parseInt(failureMatch[1], 10);
found = true;
}

return found ? { success, failure } : null;
}

/**
* NotificationDetail displays the full longform content of a notification
* along with all metadata. Used inside the NotificationBell dropdown
* when a user wants to see complete notification details.
*
* Requirements: 4.1, 4.2, 4.3, 4.4
*/
export function NotificationDetail({ notification, onBack }: NotificationDetailProps) {
const notificationType = notification.notificationType || notification.type || 'unknown';
const isBulk = notificationType === 'template_upload_completed';
const bulkCounts = isBulk ? parseBulkCounts(notification.message) : null;

return (
<div className="p-3">
{/* Back button */}
<button
data-testid="notification-detail-back"
aria-label="Back to notifications"
onClick={onBack}
className="flex items-center gap-1 text-xs text-outfront-purple hover:text-outfront-purple mb-2"
>
<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
</svg>
Back
</button>

{/* Longform title (Requirement 4.1) */}
<h4
data-testid="notification-detail-title"
className="text-sm font-semibold text-neutral-900 mb-1"
>
{notification.title}
</h4>

{/* Longform message (Requirement 4.1) */}
<p
data-testid="notification-detail-message"
className="text-xs text-neutral-600 whitespace-pre-wrap mb-3"
>
{notification.message}
</p>

{/* Bulk operation breakdown (Requirement 4.3) */}
{isBulk && bulkCounts && (
<div
data-testid="notification-detail-bulk-breakdown"
className="mb-3 rounded bg-neutral-50 border border-neutral-200 p-2 text-xs"
>
<p className="font-medium text-neutral-900 mb-1">Upload Summary</p>
<div className="flex gap-3">
{bulkCounts.success > 0 && (
<span className="text-green-700">
✓ {bulkCounts.success} created
</span>
)}
{bulkCounts.failure > 0 && (
<span className="text-red-700">
✗ {bulkCounts.failure} failed
</span>
)}
</div>
</div>
)}

{/* Metadata section (Requirement 4.2) */}
<div className="border-t border-neutral-200 pt-2 space-y-1 text-[11px] text-neutral-600">
{/* Timestamp */}
<div className="flex items-center gap-1">
<span className="font-medium text-neutral-600">Time:</span>
<span data-testid="notification-detail-timestamp">
{formatRelativeTime(notification.createdAt)}
</span>
</div>

{/* Notification type */}
<div className="flex items-center gap-1">
<span className="font-medium text-neutral-600">Type:</span>
<span data-testid="notification-detail-type">
{formatNotificationType(notificationType)}
</span>
</div>

{/* Related hold request */}
{notification.relatedHoldRequestId && (
<div className="flex items-center gap-1">
<span className="font-medium text-neutral-600">Hold Request:</span>
<span
data-testid="notification-detail-hold-request"
className="text-outfront-purple"
>
{notification.relatedHoldRequestId}
</span>
</div>
)}

{/* Related line item */}
{notification.relatedLineItemId && (
<div className="flex items-center gap-1">
<span className="font-medium text-neutral-600">Line Item:</span>
<span
data-testid="notification-detail-line-item"
className="text-outfront-purple"
>
{notification.relatedLineItemId}
</span>
</div>
)}
</div>
</div>
);
}
